import { useApi } from '@/hooks/useApi';
import {
  type CoachPermissionFlag,
  type CoachPermissions,
  type PermissionKey,
  type Permissions,
  useAuthStore,
} from '@/stores/authStore';
import { useCallback, useEffect } from 'react';

interface ActivePermissionLoad {
  sessionKey: string;
  promise: Promise<void>;
}

let activePermissionLoad: ActivePermissionLoad | null = null;

function permissionSessionKey(user: {
  id: string;
  active_profile_id?: string | null;
  active_profile?: { id: string } | null;
}): string {
  return `${user.id}:${user.active_profile_id ?? user.active_profile?.id ?? 'no-profile'}`;
}

/**
 * Hook to manage and check user permissions (Base44 aligned)
 * Loads permissions from the API and provides helpers to check access
 */
export function usePermissions() {
  const { api } = useApi();
  const {
    permissions,
    coachPermissions,
    permissionsLoading,
    setPermissions,
    setCoachPermissions,
    setPermissionsLoading,
    user,
  } = useAuthStore();

  // Load permissions from API
  const loadPermissions = useCallback(async () => {
    const requestedUser = useAuthStore.getState().user;
    if (!requestedUser) return;
    const requestedSessionKey = permissionSessionKey(requestedUser);
    if (activePermissionLoad?.sessionKey === requestedSessionKey) {
      return activePermissionLoad.promise;
    }

    setPermissionsLoading(true);
    const load: ActivePermissionLoad = {
      sessionKey: requestedSessionKey,
      promise: Promise.resolve(),
    };
    activePermissionLoad = load;
    load.promise = (async () => {
      try {
        const response = await api.get<{
          permissions: Permissions;
          coach_permissions: CoachPermissions | null;
        }>('/auth/permissions');

        const currentUser = useAuthStore.getState().user;
        if (
          response.success &&
          response.data?.permissions &&
          currentUser &&
          permissionSessionKey(currentUser) === load.sessionKey
        ) {
          setPermissions(response.data.permissions);
          setCoachPermissions(response.data.coach_permissions ?? null);
        }
      } catch (error) {
        console.error('Failed to load permissions:', error);
      } finally {
        if (activePermissionLoad === load) {
          activePermissionLoad = null;
          const currentUser = useAuthStore.getState().user;
          if (currentUser && permissionSessionKey(currentUser) === load.sessionKey) {
            setPermissionsLoading(false);
          }
        }
      }
    })();
    return load.promise;
  }, [api, setPermissions, setCoachPermissions, setPermissionsLoading]);

  // Check if user has a specific permission
  const hasPermission = useCallback(
    (permission: PermissionKey): boolean => {
      if (!permissions) return false;
      return permissions[permission] === true;
    },
    [permissions]
  );

  // Check if user has any of the given permissions
  const hasAnyPermission = useCallback(
    (permissionList: PermissionKey[]): boolean => {
      if (!permissions) return false;
      return permissionList.some((p) => permissions[p] === true);
    },
    [permissions]
  );

  // Check if user has all of the given permissions
  const hasAllPermissions = useCallback(
    (permissionList: PermissionKey[]): boolean => {
      if (!permissions) return false;
      return permissionList.every((p) => permissions[p] === true);
    },
    [permissions]
  );

  // FEAT-APR08-002 — check a raw coach permission flag
  const hasCoachFlag = useCallback(
    (flag: CoachPermissionFlag): boolean => {
      if (!coachPermissions) return false;
      return coachPermissions[flag] === true;
    },
    [coachPermissions]
  );

  // Get the effective role
  const effectiveRole = user?.role;

  // Load permissions on mount if user is authenticated
  useEffect(() => {
    if (user && !permissions) {
      loadPermissions();
    }
  }, [user, permissions, loadPermissions]);

  return {
    permissions,
    coachPermissions,
    loading: permissionsLoading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasCoachFlag,
    loadPermissions,
    effectiveRole,
  };
}

/**
 * Permission keys for PRD v2.1-aligned navigation items
 */
export const NAV_PERMISSIONS = {
  // Admin nav items
  adminDashboard: 'view_system_metrics' as PermissionKey,
  adminUsers: 'manage_users' as PermissionKey,
  adminProgramming: 'upload_programming' as PermissionKey,
  adminAssets: 'access_assets' as PermissionKey,
  adminCommissions: 'view_commissions' as PermissionKey,
  adminApprovals: 'approve_affiliates' as PermissionKey,

  // Agent nav items
  agentDashboard: 'view_commissions' as PermissionKey,
  agentCommissions: 'view_commissions' as PermissionKey,
  agentAssets: 'access_assets' as PermissionKey,

  // Affiliate nav items
  affiliateDashboard: 'view_leaderboard' as PermissionKey,
  affiliateMembers: 'manage_users' as PermissionKey,
  affiliateEvents: 'create_events' as PermissionKey,
  affiliateBilling: 'manage_billing' as PermissionKey,

  // Member nav items
  memberDashboard: 'log_scores' as PermissionKey,
  memberEvents: 'sign_up_events' as PermissionKey,
  memberLeaderboard: 'view_leaderboard' as PermissionKey,

  // Individual nav items
  individualDashboard: 'log_scores' as PermissionKey,
  individualBilling: 'manage_billing' as PermissionKey,
  individualLeaderboard: 'view_leaderboard' as PermissionKey,
};
